type LegacyRedirectSurface = "directory" | "calendar";

/**
 * Resolve the Member Portal URL a "Register" click should open for a
 * legacy-redirect event.
 *
 * Preference order (best → worst):
 *   1. Session-level deep-link (`memberPortalRegistrationUrl`) when
 *      provided. This bypasses MP's radio-button session picker and
 *      drops the user straight onto the SelectUser page. Resolved at
 *      Craft sync time via MP's `/api/hub/sessionByCraftEventId`.
 *   2. Course-level overview page keyed by `legacyCourseId`. User still
 *      has to pick a session manually but at least lands on the right
 *      course.
 *   3. Catalog / calendar fallback when neither is known.
 */
export function buildLegacyEventUrl(
  surface: LegacyRedirectSurface,
  legacyCourseId: number | null | undefined,
  memberPortalRegistrationUrl?: string | null,
  memberPortalCompanyId?: number | null,
): string {
  const base = import.meta.env.VITE_MEMBER_PORTAL_URL ?? "";

  if (memberPortalRegistrationUrl) {
    // Append &SelectedCompanyId=X when we know the user's active
    // company in Member Portal. The session URL stored at sync time
    // only knows the session half — `?sessionId=X` — because Craft
    // sync runs once for the whole system, not per-user. The company
    // half gets composed here at click time using the value cached on
    // the active company during remote-company sync.
    const suffix =
      memberPortalCompanyId != null
        ? `${memberPortalRegistrationUrl.includes("?") ? "&" : "?"}SelectedCompanyId=${memberPortalCompanyId}`
        : "";
    return `${base}${memberPortalRegistrationUrl}${suffix}`;
  }

  if (legacyCourseId != null) {
    return `${base}/Course/Course/CourseOverview?CourseId=${legacyCourseId}`;
  }

  return surface === "calendar"
    ? `${base}/Course/Course/Calendar`
    : `${base}/Course/Course/CourseCatalog`;
}
